
import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SLEntity, SLEntityType, SLEntityVersion } from './entity';
import { Apollo } from 'apollo-angular';
import { map as _map } from 'lodash';
import { SLMovement } from './movement';

const EntitiesQuery = require('./gql/entities.query.gql');
const EntityQuery = require('./gql/entity.query.gql');
const EntityLatestStateQuery = require('./gql/entity-latest-state.query.gql');
const EntityVersionsQuery = require('./gql/entity-versions.query.gql');
const EntityVersionQuery = require('./gql/entity-version.query.gql');
const EntityVersionBySignatureQuery = require('./gql/entity-version-by-signature.query.gql');

const createSLEntityMutation = require('./gql/create-entity.mutation.gql');
const updateSLEntityMutation = require('./gql/update-entity.mutation.gql');
const deleteSLEntityMutation = require('./gql/delete-entity.mutation.gql');

const createSLDraftMovementMutation = require('./gql/create-draft-movement.mutation.gql');
const deleteSLDraftMovementMutation = require('./gql/delete-draft-movement.mutation.gql');

const createSLMovementMutation = require('./gql/create-movement.mutation.gql');
const deleteSLMovementMutation = require('./gql/delete-movement.mutation.gql');

const previewSLMovementQuery = require('./gql/preview-movement.query.gql');

export interface SLEntitysQueryResult {
  entities: SLEntity[]
  entitiesCount: number
}

export type SLEntityOrderBy = 'NAME' | 'CREATED_AT';
export type SLEntityOrderByDirection = 'ASC' | 'DESC';

export class SLEntityFilter {
  offset: number
  limit: number

  search = '';
  orderBy: SLEntityOrderBy = 'NAME';
  orderDir: SLEntityOrderByDirection = 'ASC';
  deleted: false;
  type?: SLEntityType;
  hasMovements?: boolean;
}

export interface SLEntityLatestState {
  id: string
  version: number
  deleted: boolean
}


@Injectable()
export class SLEntityService {

  constructor(private apollo: Apollo) { }

  entities(filter: SLEntityFilter): Observable<SLEntitysQueryResult> {
    return this.apollo
      .query({
        query: EntitiesQuery,
        variables: {
          filter
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map((result) => {
        let items = result.data['slEntities']['items'];
        let total = result.data['slEntities']['total'];

        items = _map(items, item => new SLEntity(item));

        return {
          entities: items,
          entitiesCount: total,
        };
      }));
  }

  entity(id: string): Observable<SLEntity> {
    return this.apollo
      .query({
        query: EntityQuery,
        variables: {
          id
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => result.data['slEntity'] ? new SLEntity(result.data['slEntity']) : null));
  }

  entityLatestState(id: string): Observable<SLEntityLatestState> {
    return this.apollo
      .query({
        query: EntityLatestStateQuery,
        variables: {
          id
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => result.data['slEntity'] ? new SLEntity(result.data['slEntity']) : null));
  }

  entityVersions(id: string): Observable<SLEntityVersion[]> {
    return this.apollo
      .query({
        query: EntityVersionsQuery,
        variables: {
          id
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => _map(result.data['slEntityVersions'], item => new SLEntityVersion(item))));
  }

  entityVersion(id: string, version: number): Observable<SLEntityVersion> {
    return this.apollo
      .query({
        query: EntityVersionQuery,
        variables: {
          id,
          version
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => result.data['slEntityVersion'] ? new SLEntityVersion(result.data['slEntityVersion']) : null));
  }

  entityVersionBySignature(signature: string): Observable<SLEntityVersion> {
    return this.apollo
      .query({
        query: EntityVersionBySignatureQuery,
        variables: {
          signature
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => result.data['slEntityVersionBySignature'] ? new SLEntityVersion(result.data['slEntityVersionBySignature']): null));
  }

  create(entity: SLEntity): Observable<SLEntity> {
    return this.apollo
      .mutate({
        mutation: createSLEntityMutation,
        variables: {
          input: entity.toCreateSLEntityInput()
        }
      }).pipe(
      map(result => new SLEntity(result.data['createSLEntity'])));
  }

  update(id: string, entity: SLEntity): Observable<SLEntity> {
    return this.apollo
      .mutate({
        mutation: updateSLEntityMutation,
        variables: {
          id,
          input: entity.toUpdateSLEntityInput()
        }
      }).pipe(
      map(result => new SLEntity(result.data['updateSLEntity'])));
  }

  delete(id: string): Observable<SLEntity> {
    return this.apollo
      .mutate({
        mutation: deleteSLEntityMutation,
        variables: {
          id
        }
      }).pipe(
      map(result => new SLEntity(result.data['deleteSLEntity'])));
  }

  createDraftMovement(entity: SLEntity, movement: SLMovement): Observable<SLMovement> {
    return this.apollo
      .mutate({
        mutation: createSLDraftMovementMutation,
        variables: {
          input: movement.toCreateSLMovementInput(entity, null),
        }
      }).pipe(
      map(result => new SLMovement(result.data['createSLDraftMovement'])));
  }

  deleteDraftMovement(id: string): Observable<string> {
    return this.apollo
      .mutate({
        mutation: deleteSLDraftMovementMutation,
        variables: {
          id,
        }
      }).pipe(
      map(result => result.data['deleteSLDraftMovement']));
  }

  createMovement(entity: SLEntity, movement: SLMovement, draftMovementId?: string): Observable<SLEntity> {
    return this.apollo
      .mutate({
        mutation: createSLMovementMutation,
        variables: {
          input: movement.toCreateSLMovementInput(entity, draftMovementId),
        }
      }).pipe(
      map(result => new SLEntity(result.data['createSLMovement'])));
  }

  deleteMovement(entityId: string, id: string): Observable<SLEntity> {
    return this.apollo
      .mutate({
        mutation: deleteSLMovementMutation,
        variables: {
          entityId,
          id,
        }
      }).pipe(
      map(result => new SLEntity(result.data['deleteSLMovement'])));
  }

  previewMovement(entity: SLEntity, movement: SLMovement, draftMovementId?: string) {
    return this.apollo
      .query({
        query: previewSLMovementQuery,
        variables: {
          input: movement.toCreateSLMovementInput(entity, draftMovementId),
        },
        fetchPolicy: 'no-cache'
      }).pipe(
      map(result => {
        const data = result.data['previewSLMovement'];

        const entity = new SLEntityVersion(data.entity);
        const movement = new SLMovement(data.movement);

        entity.movementsLedger = data.movementsLedger;

        return {
          entity,
          movement,
        }
      }));
  }
}
